import clsx from "clsx";
import React from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { KevelCampaignControl } from "web/react/components/kevel-campaign-control/kevel-campaign-control";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { setLoading } from "web/redux/ducks/feed-show-more";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { numberFormat } from "web/script/modules/formats";
import globals from "web/script/modules/globals";
import history from "web/script/utils/history";
import * as styles from "./feeds-show-more.css";

interface ProgressBarProps {
    currentNumber: number;
    maximumNumber: number;
}

function ProgressBar({ currentNumber = 0, maximumNumber = 1 }: ProgressBarProps): JSX.Element {
    const progress = currentNumber / maximumNumber;
    return (
        <div className={styles.progressBar} role="presentation">
            <span
                className={styles.progressBarInner}
                style={{
                    width: progress * 100 + "%",
                }}
            />
        </div>
    );
}

export function FeedsShowMore(): JSX.Element {
    const { visibleProductCount, totalProductCount, showMoreNextUrl, retailerSlug, isLoading } =
        useReduxSelector((state: ReduxStoreState) => state.feedShowMore);

    const dispatch = useDispatch();
    let messageText = "";
    if (totalProductCount > 0 || visibleProductCount === 0) {
        // "Showing {current_product_count} of {total_product_count}"
        messageText = gettext("feed_pagination.summary_showing_products", {
            current_product_count: numberFormat(visibleProductCount),
            total_product_count: numberFormat(totalProductCount),
        });
    }

    function showMore(): void {
        if (!showMoreNextUrl) {
            return;
        }

        const pageViewId = history.getCurrentState().pageViewId;
        history.pushState(new URL(showMoreNextUrl, globals.window.location.href), {
            saveScrollPosition: false,
            data: { pageViewId },
        });
        dispatch(setLoading(true));
    }

    return (
        <div className={styles.wrapper}>
            <Text className={styles.progressMessageText} key={"show_more"} textStyle={"small-link"}>
                {messageText}
            </Text>
            <ProgressBar currentNumber={visibleProductCount} maximumNumber={totalProductCount} />
            {showMoreNextUrl && (
                <KevelCampaignControl
                    contextType={"feeds_show_more"}
                    beforeOnClick={() =>
                        analytics.event("feeds_show_more", "click_show_more_button", "click")
                    }
                    defaultAction={showMore}
                    onContinueClick={showMore}
                    analyticsEventLabel={"feeds_show_more"}
                    options={{
                        action: "feeds_show_more_click",
                        heimdallFlags: ["eac_desktop_feeds_show_more_signup_block"],
                        retailerSlug,
                    }}
                    continueOptionHref={showMoreNextUrl}
                >
                    <VStack align="center" justify="center" spacing="xs">
                        {/* onClick is handled by KevelCampaignControl wrapper */}
                        <Button
                            title={gettext("general.show_more")}
                            variant={"secondary"}
                            height={"fit-content"}
                            width={"full"}
                            href={showMoreNextUrl}
                        />
                        <div className={clsx(styles.loadingWrapper, styles.inlineLoadingWrapper)}>
                            {isLoading && <LoadingSpinner />}
                        </div>
                    </VStack>
                </KevelCampaignControl>
            )}
        </div>
    );
}
